import i18n from '@/i18n';

/**
 * Extract a human-readable, actionable message from an axios error.
 *
 * Priorité au message métier du backend (`data.message`, string ou tableau
 * class-validator — sinon affiché en `[object Object]` ou "Request failed
 * with status code 400"). À défaut, un mapping statut → message actionnable
 * remplace les anciens titres bruts « Erreur 401 » / « Erreur 500 ».
 */

/** Statut HTTP → message actionnable (fallback quand le backend n'a rien de mieux). */
const STATUS_MESSAGES: Record<number, string> = {
  401: 'Session expirée, reconnectez-vous.',
  403: 'Droits insuffisants pour cette action.',
  404: 'Ressource introuvable.',
  429: 'Trop de requêtes, réessayez dans un instant.',
};

/**
 * Messages par défaut NestJS/HTTP (non métier) : quand le backend renvoie
 * littéralement "Unauthorized" ou "Internal server error", le mapping
 * actionnable est plus utile que ce texte technique. Les messages métier
 * (409 conflit, 422 validation…) gardent la priorité.
 */
const GENERIC_SERVER_MESSAGES = new Set([
  'unauthorized',
  'forbidden',
  'not found',
  'too many requests',
  'internal server error',
  'bad gateway',
  'service unavailable',
  'gateway timeout',
]);

export function formatApiError(err: unknown): { title: string; detail: string } {
  const e = err as any;
  const status: number | undefined = e?.response?.status;
  const data = e?.response?.data;

  const serverMessage: string | undefined = Array.isArray(data?.message)
    ? data.message.join(', ')
    : typeof data?.message === 'string' && data.message.trim() !== ''
      ? data.message
      : undefined;

  const mapped: string | undefined =
    status != null
      ? STATUS_MESSAGES[status] ?? (status >= 500 ? 'Erreur serveur, réessayez.' : undefined)
      : undefined;

  let detail: string;
  if (serverMessage && !(mapped && GENERIC_SERVER_MESSAGES.has(serverMessage.trim().toLowerCase()))) {
    // Message métier du backend (409 conflit, 422 validation, class-validator…).
    detail = serverMessage;
  } else if (mapped) {
    detail = mapped;
  } else if (e?.code === 'ERR_NETWORK') {
    detail = 'Impossible de contacter le serveur, vérifiez votre connexion.';
  } else {
    detail = (e?.message as string | undefined) || 'Erreur inconnue';
  }

  return { title: i18n.t('common.error'), detail };
}

/**
 * Message d'erreur d'un appel API, sans titre ni mapping de statut.
 *
 * Axios rend `Error: Request failed with status code 409` — une phrase qui ne
 * dit rien à personne. Le serveur, lui, a répondu quelque chose d'actionnable
 * (« Confirmation expirée — relancer la préversion »), et c'est cela qu'il
 * faut montrer. Nest renvoie `message` en chaîne, ou en tableau quand la
 * validation des DTO a plusieurs griefs.
 */
export function apiErrorMessage(error: unknown, fallback?: string): string | undefined {
  const payload = (error as { response?: { data?: { message?: unknown } } })?.response?.data
    ?.message;
  if (typeof payload === 'string' && payload.trim().length > 0) return payload;
  if (Array.isArray(payload)) {
    const joined = payload.filter((m) => typeof m === 'string').join(' · ');
    if (joined.length > 0) return joined;
  }
  if (error instanceof Error && error.message) return fallback ?? error.message;
  return fallback;
}
